import React from 'react';
import ToggleRow from './ToggleRow';
import DeliveryDistanceFeeCard from './DeliveryDistanceFeeCard';

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const DeliverySettingsCard = ({ settings, onToggle, onChange }) => (
  <div className="bg-white rounded-xl shadow p-6">
    <h3 className="font-semibold mb-4">配送方式</h3>
    <div className="space-y-4">
      <ToggleRow
        label="外送配送"
        description="开启后 H5 用户可选择外送"
        checked={!!settings.enable_delivery}
        onToggle={() => onToggle('enable_delivery')}
      />
      <ToggleRow
        label="到店自提"
        description="开启后 H5 用户可选择到店自提"
        checked={!!settings.enable_pickup}
        onToggle={() => onToggle('enable_pickup')}
        bordered
      />
    </div>

    {/* 以下运费/时效设置只在开启外送时才有意义 */}
    {!!settings.enable_delivery && (
      <div className="mt-5 pt-5 border-t border-gray-100 space-y-5">

        <div>
          <h4 className="text-sm font-semibold text-gray-600 mb-3">运费设置</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>免运费门槛 (USD)</label>
              <input
                type="number" step="0.01" min="0"
                className={inputClass}
                value={settings.free_shipping_threshold ?? ''}
                onChange={e => onChange('free_shipping_threshold', e.target.value)}
                placeholder="留空=不设免运费"
              />
            </div>
            <div>
              <label className={labelClass}>基础运费 (USD)</label>
              <input
                type="number" step="0.01" min="0"
                className={inputClass}
                value={settings.base_delivery_fee ?? ''}
                onChange={e => onChange('base_delivery_fee', e.target.value)}
                placeholder="留空=运费按0元"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            订单商品小计达到免运费门槛时不收运费，否则收取基础运费。两项都留空则不收运费。
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <ToggleRow
            label="运费由第三方物流现场结算"
            description="开启后，系统不计算、不收取任何运费（固定运费/按距离计费都会失效），客户实付金额只包含商品款，运费由客户和配送员当面结清"
            checked={!!settings.delivery_fee_collected_externally}
            onToggle={() => onToggle('delivery_fee_collected_externally')}
          />
          {!!settings.delivery_fee_collected_externally && (
            <>
              <p className="text-xs text-amber-700 mt-2">
                ⚠️ 已开启：下方"免运费门槛/基础运费"和"按距离计费"这两项设置暂时都不会生效
              </p>

              {/* 三个可选触发条件——各自独立开关，商家可以只用其中一个、几个，或者都不用。
                  都不启用的话，代表这个功能对所有外送订单无条件生效 */}
              <div className="mt-4 pt-4 border-t border-amber-200 space-y-4">
                <p className="text-xs text-gray-500">
                  可选：给这个功能加上触发条件，只有勾选的条件全部满足，才会使用这个模式；不满足则回退到正常运费计算。不勾选任何条件，代表所有外送订单都无条件适用。
                </p>

                {/* 金额门槛条件 */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                      checked={!!settings.external_fee_amount_condition_enabled}
                      onChange={() => onToggle('external_fee_amount_condition_enabled')}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">订单金额达到门槛</span>
                  </label>
                  {!!settings.external_fee_amount_condition_enabled && (
                    <input type="number" step="0.01" min="0"
                      className={`${inputClass} mt-2 max-w-xs`}
                      value={settings.external_fee_min_amount ?? ''}
                      onChange={e => onChange('external_fee_min_amount', e.target.value)}
                      placeholder="订单金额门槛 (USD)" />
                  )}
                </div>

                {/* 会员身份条件 */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                      checked={!!settings.external_fee_vip_condition_enabled}
                      onChange={() => onToggle('external_fee_vip_condition_enabled')}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">会员身份</span>
                  </label>
                  {!!settings.external_fee_vip_condition_enabled && (
                    <select
                      className={`${inputClass} mt-2 max-w-xs`}
                      value={settings.external_fee_member_condition || ''}
                      onChange={e => onChange('external_fee_member_condition', e.target.value)}>
                      <option value="">请选择</option>
                      <option value="vip">必须是VIP</option>
                      <option value="agent">必须是代理</option>
                      <option value="vip_or_agent">VIP或代理，满足其一即可</option>
                    </select>
                  )}
                </div>

                {/* 件数门槛条件 */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                      checked={!!settings.external_fee_item_count_condition_enabled}
                      onChange={() => onToggle('external_fee_item_count_condition_enabled')}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">订单件数达到门槛</span>
                  </label>
                  {!!settings.external_fee_item_count_condition_enabled && (
                    <input type="number" step="1" min="0"
                      className={`${inputClass} mt-2 max-w-xs`}
                      value={settings.external_fee_min_item_count ?? ''}
                      onChange={e => onChange('external_fee_min_item_count', e.target.value)}
                      placeholder="订单件数门槛" />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* 开了"第三方物流现场结算"，距离计费这张卡片视觉上置灰、禁用交互，
            但不整个隐藏——商家还是能看到自己配置过的档位内容，只是暂时不生效，
            避免"这块设置突然消失了"的困惑 */}
        <div className={settings.delivery_fee_collected_externally ? 'opacity-40 pointer-events-none' : ''}>
          <DeliveryDistanceFeeCard settings={settings} onToggle={onToggle} onChange={onChange} />
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-600 mb-3">
            立即送时效
            <span className="text-xs text-gray-400 font-normal ml-1">（预约送不受此限制）</span>
          </h4>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className={labelClass}>营业开始时间</label>
              <input
                type="time"
                className={inputClass}
                value={settings.business_hours_start || ''}
                onChange={e => onChange('business_hours_start', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>营业结束时间</label>
              <input
                type="time"
                className={inputClass}
                value={settings.business_hours_end || ''}
                onChange={e => onChange('business_hours_end', e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            结束时间早于开始时间视为跨夜营业（如 22:00~02:00）。两项都留空表示不限制营业时间。
          </p>
          <ToggleRow
            label='开启"立即送"'
            description="临时关闭后，H5端只显示预约送（上午/下午/晚上），不显示立即送选项"
            checked={!!settings.enable_instant_delivery}
            onToggle={() => onToggle('enable_instant_delivery')}
            bordered
          />
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-600 mb-3">预约送优惠</h4>
          <ToggleRow
            label="预约送免运费"
            description="开启后，客户选择预约送（非立即送）时一律免收运费，不受免运费门槛限制，用来引导客户多选预约送"
            checked={!!settings.free_shipping_for_scheduled}
            onToggle={() => onToggle('free_shipping_for_scheduled')}
          />
        </div>

      </div>
    )}
  </div>
);

export default DeliverySettingsCard;